// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldClickable } from "@insite/mobius/FormField";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconSearch from "@turnerBlueprint/XDL/Icons/IconSearch";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { styledScrollbar } from "@turnerBlueprint/XDL/Utilities";
import color from "@turnerBlueprint/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@turnerBlueprint/XDL/Utilities/links";
import { mergeWithCss } from "@turnerBlueprint/XDL/Utilities/mergeWithCss";
import styled, { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui9",
        },
        text: {
            default: "text",
            hover: "common.ui1",
        },
    };

    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 12px;
                padding: 8px;
                line-height: 18px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;
        box-sizing: border-box;

        @media print {
            display: block;
            margin-left: 16px;
            padding-top: 12px;
            padding-bottom: 12px;
            float: left;
            width: 100%;
        }
    `,
};

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    linkStyles?: LinkPresentationProps,
    typographyStyles?: TypographyPresentationProps,
    iconStyles?: IconPresentationProps,
    iconPosition: "left" | "right" = "left",
) => {
    const marginPosition = (linkStyles?.icon?.position || iconPosition) === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: mergeWithCss(
                    {
                        size: 12,
                        lineHeight: "13px",
                        weight: fontConfig.weight.bold,
                        css: css`
                            letter-spacing: 0;
                        `,
                    },
                    typographyStyles,
                ),
                icon: {
                    iconProps: mergeWithCss(
                        {
                            src: icon,
                            size: 12,
                            css: css`
                                ${marginPosition}: 8px;
                                opacity: 0.66;

                                &,
                                & svg {
                                    height: auto;
                                }
                            `,
                        },
                        iconStyles,
                    ),
                    position: linkStyles?.icon?.position || iconPosition,
                },
                css: css`
                    &:not([disabled]):hover {
                        &,
                        & > ${TypographyStyle}, & ${IconWrapper} {
                            color: ${color("common.ui9")};
                        }
                    }

                    &[disabled] {
                        & > ${TypographyStyle}, & ${IconWrapper} {
                            color: ${color("common.ui9")};
                            cursor: not-allowed;
                        }
                        & > ${TypographyStyle} {
                            opacity: 0.66;
                        }
                    }
                `,
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...iconButtonCommonStyles(
            iconSrc,
            12,
            {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        line-height: 12px;
                    `,
                },
            },
            undefined,
            {
                css: css`
                    margin: 0 10px;
                    display: flex;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            margin-top: 24px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
        sizeVariant: "small",
    },
    submitButton: {
        sizeVariant: "small",
    },
};

export const searchBoxFieldClickableStyles: TextFieldProps = {
    iconProps: {
        src: IconSearch,
        color: "common.ui1",
    },
    cssOverrides: {
        formInputWrapper: css`
            ${FormFieldClickable} {
                padding: 10px;
                background-color: ${color("common.ui8")};

                &:not(:disabled):hover {
                    background-color: ${color("common.ui9")};
                }

                &:disabled ${IconWrapper} {
                    color: ${color("common.disabled")};
                }

                ${IconWrapper} {
                    position: relative;
                    height: auto;
                    width: auto;
                    top: auto;
                    right: auto;
                    padding: 0;
                    svg {
                        height: auto;
                        width: 15px;
                    }
                }
            }
        `,
    },
};

export const CenteringWrapper = styled(StyledWrapper)`
    height: 500px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${LoadingSpinnerStyle} {
        margin: auto;
    }
`;

export const loadingText: TypographyProps = {
    weight: fontConfig.weight.bold,
    css: css`
        display: inline-block;
        text-align: left;
        min-width: 22px;

        &::after {
            overflow: hidden;
            display: inline-block;
            vertical-align: bottom;
            animation: ellipsis steps(4, end) 700ms infinite 1s;
            content: "\\2026";
            width: 0;
            color: ${color("common.text")};
        }

        @keyframes ellipsis {
            to {
                width: 1.25em;
            }
        }
    `,
};
